import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Button, H3, H4, Spinner } from '../../primitives';
import { Spacer } from '../../helpers'; // @ts-ignore
import { Form } from 'react-stages'; // @ts-ignore
import Textarea, { isValid as isValidTextarea } from './fields/Textarea'; // @ts-ignore
import InputFile, { isValid as isValidInputFile } from './fields/InputFile'; // @ts-ignore
import Checkbox, { isValid as isValidCheckbox } from './fields/Checkbox'; // @ts-ignore
import Input, { isValid as isValidInput } from './fields/Input'; // @ts-ignore
import FieldError from './fields/parts/FieldError';
import { animations, colors, mixins } from '../../core/styles/theme/default';
import { rem } from 'polished';
import { css } from '@emotion/react';
import i18n from './alliance-i18n';
import { navigate } from 'gatsby-link';

const StyledForm = styled.form``;

const StyledHeading = styled(H3)`
  padding-bottom: ${rem(6)};
  border-bottom: ${rem(1)} solid currentColor;
`;

const StyledSubheading = styled(H4)`
  font-weight: 600;
  display: flex;
  align-items: center;

  ${mixins.responsiveStyles({
    'font-size': { zero: 18, large: 20 },
  })}
`;

const StyledParagraph = styled.p`
  line-height: 1.5;
  margin: 0;
  font-size: ${rem(16)};
  white-space: pre-line;
`;

const StyledFieldGroup = styled.div`
  display: grid;

  ${mixins.responsiveStyles({
    'grid-template-columns': {
      zero: '1fr',
      medium: 'repeat(2, 1fr)',
    },
    'grid-gap': {
      zero: `${rem(16)}`,
      medium: `${rem(24)}`,
      ultra: `${rem(24)} ${rem(32)}`,
    },
  })}
`;

const StyledFieldGroupSingle = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  ${mixins.responsiveStyles({
    'grid-gap': {
      zero: `${rem(16)}`,
      medium: `${rem(24)}`,
    },
  })}
`;

const StyledField = styled.div`
  font-size: ${rem(16)};

  input,
  textarea {
    background: none;
    box-shadow: none;
    border: ${rem(1)} solid ${colors.grey400};
    border-radius: ${rem(2)};
    min-height: ${rem(48)};
    padding: ${rem(12)};
    margin: 0;
    width: 100%;

    ${mixins.responsiveStyles({
      'scroll-margin-top': { zero: 64, medium: 88 },
    })}

    &:focus {
      outline: ${rem(2)} solid ${colors.blueNichtverschwenden};
      outline-offset: ${rem(-1)};
    }

    &::placeholder {
      color: ${colors.grey400};
    }

    &[data-error='true'] {
      border-color: ${colors.error};
    }
  }

  textarea {
    display: block;
    resize: none;
    min-height: ${rem(300)};
  }
`;

const StyledFieldFull = styled(StyledField)`
  ${mixins.MQ('medium')} {
    grid-column: span 2;
  }
`;

const StyledButton = styled(Button)`
  min-width: 0;
`;

const StyledToggleButton = styled.button(
  ({ variant }: { variant?: string }) => css`
    cursor: pointer;
    position: relative;
    width: ${rem(32)};
    height: ${rem(32)};
    border: ${rem(2)} solid ${colors.grue800};
    background: none;
    display: block;
    margin: ${rem(12)} 0 ${rem(-44)} auto;
    padding: 0;
    border-radius: 50%;
    transition: background-color ${animations.default};

    &:before,
    &:after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: ${rem(14)};
      border-top: ${rem(2)} solid;
      border-top-color: inherit;
      transition: border-color ${animations.default};
    }

    &:after {
      transform: translate(-50%, -50%) rotate(90deg);
    }

    &:hover,
    &:focus {
      background: ${colors.grue800};

      &:before,
      &:after {
        border-top-color: ${colors.white};
      }
    }

    ${variant === 'remove' &&
    css`
      display: inline-block;
      border-color: ${colors.error};
      transform: rotate(45deg);
      margin: ${rem(-1)} 0 0 ${rem(12)};

      &:hover,
      &:focus {
        background: ${colors.error};
      }
    `}
  `,
);

const StyledHr = styled.hr`
  background: currentColor;
  border: none;
  height: ${rem(1)};
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(16)};
`;

const fields = {
  text: {
    component: Input,
    isValid: isValidInput,
  },
  url: {
    component: Input,
    isValid: isValidInput,
  },
  email: {
    component: Input,
    isValid: isValidInput,
  },
  tel: {
    component: Input,
    isValid: isValidInput,
  },
  checkbox: {
    component: Checkbox,
    isValid: isValidCheckbox,
  },
  textarea: {
    component: Textarea,
    isValid: isValidTextarea,
  },
  file: {
    component: InputFile,
    isValid: isValidInputFile,
  },
};

const AllianceForm = ({
  requestUrl,
  successPageUrl,
  failurePageUrl,
  failureMailSendPageUrl,
  locale = 'de',
}: {
  requestUrl: string;
  successPageUrl: string;
  failurePageUrl: string;
  failureMailSendPageUrl: string;
  locale: string;
}) => {
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const isClient = typeof window !== 'undefined';

  const onSubmit = () => {
    const formData = new FormData();
    const rawFormData = formRef.current && new FormData(formRef.current);
    const logo = rawFormData?.get('logo');
    setIsLoading(true);

    // Remove the error property from the form fields
    if (formRef.current) {
      Array.from(formRef.current.elements)
        .filter((field) =>
          ['input', 'textarea'].includes(field.tagName.toLowerCase()),
        )
        .map((field) => {
          if (field instanceof HTMLElement) {
            field.dataset.error = 'false';
          }
        });
    }

    // Merge all form fields into FormData() object
    for (const [key, value] of Object.entries(data)) {
      formData.append(
        key,
        typeof value === 'object' ? JSON.stringify(value) : (value as string),
      );
    }
    logo && formData.append('logo', logo);
    formData.append('locale', locale);

    // Send request to the API endpoint
    fetch(requestUrl, {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        setIsLoading(false);

        if (response.ok) {
          isClient && void navigate(successPageUrl);
        } else {
          return response.json();
        }
      })
      .then((errorObj) => {
        if (!errorObj) return;

        switch (errorObj.type) {
          case 'ParsingError':
            if (formRef.current) {
              const field = document.getElementById(
                errorObj.error.error.error
                  ? `${errorObj.error.property}[${errorObj.error.error.index}].${errorObj.error.error.error.property}`
                  : `${errorObj.error.property}`,
              );

              if (field) {
                field.dataset.error = 'true';
                field.scrollIntoView();
              }
            }
            break;
          case 'MailSendError':
            isClient && void navigate(failureMailSendPageUrl);
            break;
          default:
            isClient && void navigate(failurePageUrl);
        }
      })
      .catch(() => {
        isClient && void navigate(failurePageUrl);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <StyledForm
      ref={formRef}
      onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}
      noValidate
    >
      <Form
        id={`allianceForm-${locale}`}
        data={data}
        fields={fields}
        onChange={(payload: object) => setData(payload)}
        config={{
          fields: () => {
            return [
              {
                id: 'organisation',
                label: i18n.organisation[locale],
                type: 'text',
                locale,
                isRequired: true,
              },
              {
                id: 'address',
                label: i18n.address[locale],
                type: 'text',
                locale,
                isRequired: true,
              },
              {
                id: 'postcode',
                label: i18n.postcode[locale],
                type: 'text',
                locale,
                isRequired: true,
                customValidation: ({ data, fieldConfig }: any) => {
                  if (!data && fieldConfig.isRequired) {
                    return false;
                  }

                  if (data && !data.match(/^[1-9]\d{3}$/)) {
                    return 'INVALID_POSTCODE';
                  }

                  return true;
                },
                errorRenderer: (error: any) =>
                  FieldError({
                    text:
                      error.errorCode === 'INVALID_POSTCODE'
                        ? i18n.errors.invalidPostcode[locale]
                        : i18n.errors.requiredField[locale],
                  }),
              },
              {
                id: 'city',
                label: i18n.city[locale],
                type: 'text',
                locale,
                isRequired: true,
              },
              {
                id: 'website',
                label: i18n.website[locale],
                type: 'url',
                locale,
                placeholder: i18n.website.placeholder[locale],
                isRequired: true,
                customValidation: ({ data, fieldConfig }: any) => {
                  if (!data && fieldConfig.isRequired) {
                    return false;
                  }

                  if (
                    data &&
                    !data.match(
                      '^(ftp|http|https)://(\\w+:{0,1}\\w*@)?(\\S+)(:[0-9]+)?(/|/([\\w#!:.?+=&%@\\-/]))?$',
                    )
                  ) {
                    return 'INVALID_URL';
                  }

                  return true;
                },
                errorRenderer: (error: any) =>
                  FieldError({
                    text:
                      error.errorCode === 'INVALID_URL'
                        ? i18n.errors.invalidUrl[locale]
                        : i18n.errors.requiredField[locale],
                  }),
              },
              {
                id: 'logo',
                label: i18n.logo[locale],
                prefix: i18n.logo.prefix[locale],
                type: 'file',
                locale,
                isRequired: true,
                maxFileSize: 524288, // 0.5 MiB
                acceptedFileTypes: 'image/jpeg, image/png, image/svg+xml',
              },
              {
                id: 'persons',
                type: 'collection',
                init: true,
                fields: [
                  {
                    id: 'name',
                    label: i18n.persons.name[locale],
                    type: 'text',
                    locale,
                    isRequired: true,
                  },
                  {
                    id: 'role',
                    label: i18n.persons.role[locale],
                    type: 'text',
                    locale,
                    isRequired: true,
                  },
                  {
                    id: 'email',
                    label: i18n.persons.email[locale],
                    type: 'email',
                    locale,
                    isRequired: true,
                    customValidation: ({ data, fieldConfig }: any) => {
                      if (!data && fieldConfig.isRequired) {
                        return false;
                      }

                      if (
                        data &&
                        !data.match('^\\w[\\w.-]*@([\\w-]+\\.)+[\\w-]+$')
                      ) {
                        return 'INVALID_EMAIL';
                      }

                      return true;
                    },
                    errorRenderer: (error: any) =>
                      FieldError({
                        text:
                          error.errorCode === 'INVALID_EMAIL'
                            ? i18n.errors.invalidEmail[locale]
                            : i18n.errors.requiredField[locale],
                      }),
                  },
                  {
                    id: 'phone',
                    label: i18n.persons.phone[locale],
                    type: 'tel',
                    locale,
                    placeholder: '+41 XX XXX XX XX',
                    isRequired: true,
                    customValidation: ({ data, fieldConfig }: any) => {
                      if (!data && fieldConfig.isRequired) {
                        return false;
                      }

                      if (data && !data.match('^(0|\\+)[\\d- /]{9,}$')) {
                        return 'INVALID_NUMBER';
                      }

                      return true;
                    },
                    errorRenderer: (error: any) =>
                      FieldError({
                        text:
                          error.errorCode === 'INVALID_NUMBER'
                            ? i18n.errors.invalidPhone[locale]
                            : i18n.errors.requiredField[locale],
                      }),
                  },
                ],
              },
              {
                id: 'measures',
                type: 'collection',
                init: true,
                fields: [
                  {
                    id: 'title',
                    label: i18n.measures.title[locale],
                    type: 'text',
                    locale,
                    isRequired: true,
                    showCharactersCount: true,
                    maxLength: 256,
                  },
                  {
                    id: 'description',
                    label: i18n.measures.description[locale],
                    type: 'textarea',
                    locale,
                    placeholder: i18n.measures.description.placeholder[locale],
                    prefix: i18n.measures.description.prefix[locale],
                    isRequired: true,
                    showCharactersCount: true,
                    maxLength: 512,
                  },
                  {
                    id: 'impact',
                    label: i18n.measures.impact[locale],
                    type: 'textarea',
                    locale,
                    placeholder: i18n.measures.impact.placeholder[locale],
                    prefix: i18n.measures.impact.prefix[locale],
                    isRequired: true,
                    showCharactersCount: true,
                    maxLength: 512,
                  },
                  {
                    id: 'furtherInfo',
                    label: i18n.measures.furtherInfo[locale],
                    type: 'text',
                    locale,
                    prefix: i18n.measures.furtherInfo.prefix[locale],
                    showCharactersCount: true,
                    maxLength: 256,
                  },
                ],
              },
              {
                id: 'subscribeToNewsletter',
                label: i18n.subscribeToNewsletter[locale],
                type: 'checkbox',
                locale,
              },
            ];
          },
        }}
        render={({ actionProps, fieldProps }: any) => (
          <>
            <StyledHeading>{i18n.organisationHeading[locale]}</StyledHeading>
            <Spacer space="delta" />
            <StyledFieldGroup>
              <StyledField>{fieldProps.fields.organisation}</StyledField>
              <StyledField>{fieldProps.fields.address}</StyledField>
              <StyledField>{fieldProps.fields.postcode}</StyledField>
              <StyledField>{fieldProps.fields.city}</StyledField>
              <StyledFieldFull>{fieldProps.fields.website}</StyledFieldFull>
              <StyledFieldFull>{fieldProps.fields.logo}</StyledFieldFull>
            </StyledFieldGroup>
            <Spacer space="iota" />
            <StyledHeading>{i18n.personHeading[locale]}</StyledHeading>
            <Spacer space="delta" />
            <>
              {fieldProps.fields.persons?.map(
                (subFields: any, index: number) => (
                  <div key={index}>
                    {index > 0 ? <Spacer space="iota" /> : null}
                    <StyledSubheading>
                      {`${i18n.personSubheading[locale]} ${index + 1}`}
                      {index > 0 ? (
                        <StyledToggleButton
                          type="button"
                          variant="remove"
                          aria-label={i18n.aria.removePerson[locale]}
                          onClick={() =>
                            fieldProps.onCollectionAction(
                              'persons',
                              'remove',
                              index,
                            )
                          }
                        />
                      ) : null}
                    </StyledSubheading>
                    <Spacer space="delta" />
                    <StyledFieldGroup>
                      <StyledField>{subFields.name}</StyledField>
                      <StyledField>{subFields.role}</StyledField>
                      <StyledField>{subFields.email}</StyledField>
                      <StyledField>{subFields.phone}</StyledField>
                    </StyledFieldGroup>
                  </div>
                ),
              )}
              {fieldProps.fields.persons?.length < 3 ? (
                <StyledToggleButton
                  type="button"
                  aria-label={i18n.aria.addPerson[locale]}
                  onClick={() =>
                    fieldProps.onCollectionAction('persons', 'add')
                  }
                />
              ) : null}
            </>
            <Spacer space="iota" />
            <StyledHeading>{i18n.measuresHeading[locale]}</StyledHeading>
            <Spacer space="delta" />
            <StyledParagraph>
              {i18n.measuresDescription[locale]}
            </StyledParagraph>
            <Spacer space="delta" />
            <>
              {fieldProps.fields.measures?.map(
                (subFields: any, index: number) => (
                  <div key={index}>
                    {index > 0 ? <Spacer space="iota" /> : null}
                    <StyledSubheading>
                      {`${i18n.measuresSubheading[locale]} ${index + 1}`}
                      {index > 0 ? (
                        <StyledToggleButton
                          type="button"
                          variant="remove"
                          aria-label={i18n.aria.removeMeasure[locale]}
                          onClick={() =>
                            fieldProps.onCollectionAction(
                              'measures',
                              'remove',
                              index,
                            )
                          }
                        />
                      ) : null}
                    </StyledSubheading>
                    <Spacer space="delta" />
                    <StyledFieldGroupSingle>
                      <StyledField>{subFields.title}</StyledField>
                      <StyledField>{subFields.description}</StyledField>
                      <StyledField>{subFields.impact}</StyledField>
                      <StyledField>{subFields.furtherInfo}</StyledField>
                    </StyledFieldGroupSingle>
                  </div>
                ),
              )}
              {fieldProps.fields.measures?.length < 3 ? (
                <StyledToggleButton
                  type="button"
                  aria-label={i18n.aria.addMeasure[locale]}
                  onClick={() =>
                    fieldProps.onCollectionAction('measures', 'add')
                  }
                />
              ) : null}
            </>
            <Spacer space="iota" />
            <StyledHr />
            <Spacer space="zeta" />
            <StyledField>{fieldProps.fields.subscribeToNewsletter}</StyledField>
            <Spacer space="iota" />
            <StyledButtonWrapper>
              <StyledButton
                buttonText={i18n.submitButton[locale]}
                aria-label={i18n.aria.submitForm[locale]}
                type="submit"
                onClick={() => actionProps.handleActionClick(onSubmit, true)}
                disabled={isLoading}
              />
              {isLoading && <Spinner size={24} />}
            </StyledButtonWrapper>
          </>
        )}
      />
    </StyledForm>
  );
};

export default AllianceForm;
